import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Card,
  Overline,
  SectionHeader,
  Tag,
  YamlBlock,
} from "@/components/cathedral/Primitives";

function toYaml(obj) {
  const lines = [];
  const walk = (o, indent = 0) => {
    Object.entries(o || {}).forEach(([k, v]) => {
      const pad = "  ".repeat(indent);
      if (v && typeof v === "object" && !Array.isArray(v)) {
        lines.push(`${pad}${k}:`);
        walk(v, indent + 1);
      } else if (Array.isArray(v)) {
        lines.push(`${pad}${k}:`);
        v.forEach((item) => {
          if (item && typeof item === "object") {
            lines.push(`${pad}  -`);
            walk(item, indent + 2);
          } else {
            lines.push(`${pad}  - ${JSON.stringify(item)}`);
          }
        });
      } else {
        lines.push(`${pad}${k}: ${JSON.stringify(v)}`);
      }
    });
  };
  walk(obj);
  return lines.join("\n");
}

export default function LedgerExplorer() {
  const [entries, setEntries] = useState([]);
  const [active, setActive] = useState(null);
  const [filter, setFilter] = useState({ node_id: "", kind: "" });
  const [nodes, setNodes] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.node_id) params.node_id = filter.node_id;
      if (filter.kind) params.kind = filter.kind;
      const r = await api.ledger(params);
      setEntries(r.entries || []);
      if (!active && r.entries?.length) setActive(r.entries[0]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.nodes().then((r) => setNodes(r.nodes || []));
  }, []);
  useEffect(() => {
    load();
  }, [filter]);

  return (
    <section data-testid="ledger-explorer" className="px-8 lg:px-12 py-12 max-w-screen-2xl">
      <SectionHeader
        overline="DIR-066 · Rebuildability From Fragments"
        title="Federation Ledger"
        description="The shared memory bucket. Every Agent Zero execution and manual write is preserved here as YAML-equivalent BSON, retrievable as forensic fragments."
      />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12">
        <aside className="lg:col-span-4 space-y-4">
          <Card className="p-6">
            <Overline>Filters</Overline>
            <div className="mt-4 space-y-3">
              <select
                data-testid="ledger-filter-node"
                value={filter.node_id}
                onChange={(e) => setFilter({ ...filter, node_id: e.target.value })}
                className="w-full border border-[var(--chrome)] rounded-none p-3 font-mono text-[11px] uppercase tracking-widest bg-[var(--void-2)]"
              >
                <option value="">All Nodes</option>
                {nodes.map((n) => (
                  <option key={n.id} value={n.id}>{n.name}</option>
                ))}
              </select>
              <select
                data-testid="ledger-filter-kind"
                value={filter.kind}
                onChange={(e) => setFilter({ ...filter, kind: e.target.value })}
                className="w-full border border-[var(--chrome)] rounded-none p-3 font-mono text-[11px] uppercase tracking-widest bg-[var(--void-2)]"
              >
                <option value="">Any Kind</option>
                <option value="execution">Execution</option>
                <option value="interaction">Interaction</option>
              </select>
            </div>
          </Card>

          <Card className="p-0 overflow-hidden">
            <header className="px-6 py-4 border-b border-[var(--chrome)] flex items-center justify-between">
              <Overline>Entries</Overline>
              <span className="font-mono text-[10px] text-[var(--text-3)]">
                {loading ? "loading..." : `${entries.length}`}
              </span>
            </header>
            <ul className="divide-y divide-slate-200 max-h-[70vh] overflow-y-auto">
              {entries.map((e) => (
                <li
                  key={e.id}
                  data-testid={`ledger-row-${e.id}`}
                  onClick={() => setActive(e)}
                  className={`px-6 py-4 cursor-pointer hover:bg-[rgba(0,240,255,0.06)] transition-colors ${
                    active?.id === e.id ? "bg-[rgba(0,240,255,0.06)] border-l-2 border-[var(--cyan)]" : "border-l-2 border-transparent"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Tag tone={e.truth_gate?.passes ? "pass" : "conflict"}>{(e.kind || "—").toUpperCase()}</Tag>
                    <span className="font-mono text-[10px] text-[var(--text-3)]">{e.node_id}</span>
                  </div>
                  <div className="text-sm text-[var(--text-2)] truncate">{e.task || e.payload?.title || e.id}</div>
                  <div className="font-mono text-[10px] text-[var(--text-3)] mt-1">
                    {new Date(e.timestamp).toLocaleString()}
                  </div>
                </li>
              ))}
              {!loading && entries.length === 0 && (
                <li className="p-6 font-mono text-xs text-[var(--text-3)]">No entries</li>
              )}
            </ul>
          </Card>
        </aside>

        <div className="lg:col-span-8">
          {active ? (
            <Card className="p-0">
              <header className="p-8 border-b border-[var(--chrome)]">
                <Overline>Entry · {active.id}</Overline>
                <h3 className="font-serif text-2xl font-light tracking-tight mt-3 text-[var(--text)]">
                  {active.task || active.payload?.title || "Ledger entry"}
                </h3>
                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <Tag tone="fact">{active.node_id}</Tag>
                  <Tag>{(active.kind || "—").toUpperCase()}</Tag>
                  {active.truth_gate && (
                    <Tag tone={active.truth_gate.passes ? "pass" : "conflict"}>
                      GATE · {active.truth_gate.final_score}
                    </Tag>
                  )}
                  <span className="font-mono text-[10px] text-[var(--text-3)]">
                    {new Date(active.timestamp).toLocaleString()}
                  </span>
                </div>
              </header>
              <YamlBlock value={toYaml(active)} />
            </Card>
          ) : (
            <Card className="p-12">
              <p className="font-serif text-3xl font-light text-[var(--text-3)]">
                Select a fragment to inspect.
              </p>
            </Card>
          )}
        </div>
      </div>
    </section>
  );
}
